/** XeAgents.ai **/
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, Loader2, PlugZap, XCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import ProvidersPanel from "./ProvidersPanel";
import type { HealthcareSettings } from "./ClinicPanel";

type Mode = "internal" | "rest" | "cliniko";

const OPERATIONS: Array<{ key: string; label: string; method: string; path: string }> = [
  { key: "listProviders", label: "List doctors", method: "GET", path: "/providers" },
  { key: "findPatient", label: "Find a patient", method: "POST", path: "/patients/search" },
  { key: "createPatient", label: "Register a new patient", method: "POST", path: "/patients" },
  { key: "findSlots", label: "Find open times", method: "POST", path: "/slots/search" },
  { key: "listAppointments", label: "List a patient's appointments", method: "POST", path: "/appointments/search" },
  { key: "book", label: "Book an appointment", method: "POST", path: "/appointments" },
  { key: "reschedule", label: "Reschedule", method: "POST", path: "/appointments/{id}/reschedule" },
  { key: "cancel", label: "Cancel", method: "POST", path: "/appointments/{id}/cancel" },
];

const ENTITIES: Array<{ key: string; label: string; fields: string[] }> = [
  { key: "patient", label: "Patient", fields: ["id", "fullName", "dob", "phone", "email"] },
  { key: "provider", label: "Doctor", fields: ["id", "name", "specialty", "location"] },
  { key: "slot", label: "Open time", fields: ["start", "providerId", "providerName"] },
  { key: "appointment", label: "Appointment", fields: ["id", "start", "providerId", "providerName", "specialty", "status", "reason"] },
];

interface TestResult {
  ok: boolean;
  note: string;
  results: Array<{ operation: string; ok: boolean; ms: number; detail: string }>;
}

function Section({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <Collapsible className="rounded-md border">
      <CollapsibleTrigger className="group flex w-full items-center justify-between p-3 text-left">
        <span>
          <span className="block text-sm font-medium">{title}</span>
          <span className="block text-xs text-muted-foreground">{hint}</span>
        </span>
        <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t p-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}

export default function ConnectorPanel({ settings }: { settings: HealthcareSettings | null }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>("internal");
  const [baseUrl, setBaseUrl] = useState("");
  const [authType, setAuthType] = useState("bearer");
  const [authHeader, setAuthHeader] = useState("");
  const [secret, setSecret] = useState("");
  const [timeoutMs, setTimeoutMs] = useState("");
  const [operations, setOperations] = useState<Record<string, { method: string; path: string }>>({});
  const [mapping, setMapping] = useState<Record<string, Record<string, string>>>({});
  const [test, setTest] = useState<TestResult | null>(null);
  const [shard, setShard] = useState("au1");
  const [businessId, setBusinessId] = useState("");
  const [concessionTypeId, setConcessionTypeId] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  useEffect(() => {
    if (!settings) return;
    const c = settings.connectorConfig ?? {};
    setMode(settings.connectorType);
    setBaseUrl(c.baseUrl ?? "");
    setShard(c.shard ?? "au1");
    setBusinessId(c.businessId ? String(c.businessId) : "");
    setConcessionTypeId(c.concessionTypeId ? String(c.concessionTypeId) : "");
    setContactEmail(c.contactEmail ?? "");
    setAuthType(c.authType ?? "bearer");
    setAuthHeader(c.authHeader ?? "");
    setTimeoutMs(c.timeoutMs ? String(c.timeoutMs) : "");
    setOperations(
      Object.fromEntries(Object.entries((c.operations ?? {}) as Record<string, { method?: string; path?: string }>).map(([k, v]) => [k, { method: v.method ?? "", path: v.path ?? "" }])),
    );
    setMapping(c.mapping ?? {});
    setSecret("");
  }, [settings]);

  const restConfig = () => ({
    baseUrl: baseUrl.trim(),
    authType,
    ...(authType === "header" ? { authHeader: authHeader.trim() } : {}),
    ...(timeoutMs.trim() ? { timeoutMs: Number(timeoutMs) } : {}),
    operations: Object.fromEntries(Object.entries(operations).filter(([, v]) => v.path.trim() || v.method.trim())),
    mapping: Object.fromEntries(Object.entries(mapping).map(([e, f]) => [e, Object.fromEntries(Object.entries(f).filter(([, v]) => v.trim()))])),
  });

  const connectorConfig = () => (mode === "cliniko" ? { shard: shard.trim().toLowerCase(), contactEmail: contactEmail.trim(), ...(businessId.trim() ? { businessId: Number(businessId) } : {}), ...(concessionTypeId.trim() ? { concessionTypeId: Number(concessionTypeId) } : {}) } : restConfig());

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { connectorType: mode };
      if (mode !== "internal") {
        body.connectorConfig = connectorConfig();
        if (secret.trim()) body.connectorSecret = secret.trim();
      }
      return (await apiRequest("PUT", "/api/healthcare/settings", body)).json();
    },
    onSuccess: () => {
      toast({ title: "Integration saved", description: mode === "rest" ? "New calls will use your system." : "New calls will use VoicePro's calendar." });
      setSecret("");
      queryClient.invalidateQueries({ queryKey: ["/api/healthcare/settings"] });
    },
    onError: (e: any) => toast({ title: "Could not save", description: e.message, variant: "destructive" }),
  });

  const runTest = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { connectorType: mode };
      if (mode !== "internal") {
        body.connectorConfig = connectorConfig();
        if (secret.trim()) body.connectorSecret = secret.trim();
      }
      return (await apiRequest("POST", "/api/healthcare/connector/test", body)).json() as Promise<TestResult>;
    },
    onSuccess: setTest,
    onError: (e: any) => {
      setTest(null);
      toast({ title: "Could not run the test", description: e.message, variant: "destructive" });
    },
  });

  const testSection = (
            <div className="space-y-3">
              <Button variant="outline" onClick={() => runTest.mutate()} disabled={runTest.isPending || (mode === "rest" ? !baseUrl.trim() : !shard.trim() || !contactEmail.trim())} data-testid="button-test-connector">
                {runTest.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}
                Test connection
              </Button>
              {test && (
                <Alert variant={test.ok ? "default" : "destructive"}>
                  <AlertTitle>{test.ok ? "Your system answered every check" : "Some checks failed"}</AlertTitle>
                  <AlertDescription>
                    <ul className="mt-2 space-y-1">
                      {test.results.map((r) => (
                        <li key={r.operation} className="flex items-center gap-2 text-sm">
                          {r.ok ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                          <span className="font-medium">{r.operation}</span>
                          <span>{r.detail}</span>
                          <span className="text-muted-foreground">({r.ms} ms)</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-xs">{test.note}</p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
  );

  const secretOptional = mode === "internal" || (mode === "rest" && authType === "none");
  const needsSecret = !secretOptional && !secret.trim() && !(settings?.hasConnectorSecret && settings.connectorType === mode);
  const validTarget =
    mode === "internal" ||
    (mode === "rest" && (baseUrl.trim().startsWith("https://") || baseUrl.trim().startsWith("http://localhost"))) ||
    (mode === "cliniko" && /^[a-z]{2}[0-9]{1,2}$/i.test(shard.trim()) && /^[^s@]+@[^s@]+.[^s@]+$/.test(contactEmail.trim()));
  const canSave = !save.isPending && validTarget && !needsSecret;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Integration: where are your patients and appointments kept?</CardTitle>
          <CardDescription>
            The receptionist reads and writes through one connection. Use VoicePro's calendar if you have nothing yet, or connect the software your clinic already uses, which then stays the single source of truth.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="Integration">
            {(
              [
                ["internal", "VoicePro calendar", "Doctors and bookings live in VoicePro, with times from Cal.com. Fastest to start."],
                ["cliniko", "Cliniko", "Native connection. Practitioners, patients and appointments stay in Cliniko. Needs a Cliniko API key."],
                ["rest", "Other software (API)", "Connect any practice-management, EMR or booking system that has a secure API, or put a small bridge in front of one that does not."],
              ] as Array<[Mode, string, string]>
            ).map(([value, title, text]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mode === value}
                onClick={() => setMode(value)}
                className={cn("rounded-lg border p-4 text-left transition-colors hover:bg-muted/50", mode === value && "border-primary bg-primary/5")}
                data-testid={`radio-connector-${value}`}
              >
                <span className="block font-medium">{title}</span>
                <span className="mt-1 block text-sm text-muted-foreground">{text}</span>
              </button>
            ))}
          </div>

          {mode === "cliniko" && (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="cl-shard">Cliniko region</Label>
                  <Input id="cl-shard" value={shard} maxLength={4} onChange={(e) => setShard(e.target.value)} placeholder="au1" data-testid="input-cliniko-shard" />
                  <p className="text-xs text-muted-foreground">The part after "api." in your Cliniko address: au1, uk1, us1, ca1 and so on.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cl-email">Contact email</Label>
                  <Input id="cl-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="admin@yourclinic.com" data-testid="input-cliniko-email" />
                  <p className="text-xs text-muted-foreground">Cliniko asks API users to identify themselves. Sent with each request.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cl-key">Cliniko API key</Label>
                  <Input id="cl-key" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={settings?.hasConnectorSecret && settings.connectorType === "cliniko" ? "Saved. Type to replace it." : "Paste it here"} data-testid="input-cliniko-key" />
                  <p className="text-xs text-muted-foreground">Create it in Cliniko under My info, Manage API keys. Use a user with only the access the front desk needs. Stored encrypted.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cl-business">Location id (optional)</Label>
                  <Input id="cl-business" inputMode="numeric" value={businessId} onChange={(e) => setBusinessId(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Only if you have more than one location" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cl-concession">Concession type id (optional)</Label>
                  <Input id="cl-concession" inputMode="numeric" value={concessionTypeId} onChange={(e) => setConcessionTypeId(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Only if Cliniko refuses new patients without one" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Callers can book practitioners who are switched on for online bookings in Cliniko. Run the test with your real key before going live.</p>
              {testSection}
            </div>
          )}

          {mode === "rest" && (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="cx-url">Address of your system's API</Label>
                  <Input id="cx-url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.yourclinicsoftware.com/voicepro" data-testid="input-connector-url" />
                  <p className="text-xs text-muted-foreground">Must be a public https address. If your software has no API of its own, point this at a small bridge or integration flow that speaks the format below.</p>
                </div>
                <div className="space-y-2">
                  <Label>How we sign in</Label>
                  <Select value={authType} onValueChange={setAuthType}>
                    <SelectTrigger data-testid="select-connector-auth">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bearer">Bearer token</SelectItem>
                      <SelectItem value="header">API key in a header</SelectItem>
                      <SelectItem value="basic">Username and password</SelectItem>
                      <SelectItem value="none">No sign-in (private network or IP allow-list)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {authType === "header" && (
                  <div className="space-y-2">
                    <Label htmlFor="cx-header">Header name</Label>
                    <Input id="cx-header" value={authHeader} onChange={(e) => setAuthHeader(e.target.value)} placeholder="X-API-Key" />
                  </div>
                )}
                {authType !== "none" && (
                  <div className="space-y-2">
                    <Label htmlFor="cx-secret">{authType === "basic" ? "Username:password" : authType === "header" ? "API key" : "Token"}</Label>
                    <Input
                      id="cx-secret"
                      type="password"
                      autoComplete="off"
                      value={secret}
                      onChange={(e) => setSecret(e.target.value)}
                      placeholder={settings?.hasConnectorSecret ? "Saved. Type to replace it." : "Paste it here"}
                      data-testid="input-connector-secret"
                    />
                    <p className="text-xs text-muted-foreground">Stored encrypted. It is never shown again or sent to the browser.</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="cx-timeout">Give up after (milliseconds)</Label>
                  <Input id="cx-timeout" inputMode="numeric" value={timeoutMs} onChange={(e) => setTimeoutMs(e.target.value.replace(/[^0-9]/g, ""))} placeholder="8000" />
                  <p className="text-xs text-muted-foreground">Callers are waiting, so keep this short. 1000 to 15000.</p>
                </div>
              </div>

              <Section title="Endpoints (optional)" hint="Only change these if your system uses different paths than the defaults.">
                <div className="space-y-2">
                  {OPERATIONS.map((op) => (
                    <div key={op.key} className="grid items-center gap-2 md:grid-cols-[200px_100px_1fr]">
                      <span className="text-sm">{op.label}</span>
                      <Select value={operations[op.key]?.method || op.method} onValueChange={(method) => setOperations({ ...operations, [op.key]: { path: operations[op.key]?.path ?? "", method } })}>
                        <SelectTrigger aria-label={`${op.label} method`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                            <SelectItem key={m} value={m}>
                              {m}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        aria-label={`${op.label} path`}
                        value={operations[op.key]?.path ?? ""}
                        onChange={(e) => setOperations({ ...operations, [op.key]: { method: operations[op.key]?.method ?? "", path: e.target.value } })}
                        placeholder={op.path}
                      />
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">{"{id}"} is replaced with the appointment id.</p>
                </div>
              </Section>

              <Section title="Field names (optional)" hint="If your system calls a field something else, say where to find it, like data.patient_id. Leave empty to use our names.">
                <div className="space-y-4">
                  {ENTITIES.map((entity) => (
                    <div key={entity.key}>
                      <p className="mb-2 text-sm font-medium">{entity.label}</p>
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {entity.fields.map((field) => (
                          <div key={field} className="space-y-1">
                            <Label className="text-xs text-muted-foreground">{field}</Label>
                            <Input
                              value={mapping[entity.key]?.[field] ?? ""}
                              onChange={(e) => setMapping({ ...mapping, [entity.key]: { ...mapping[entity.key], [field]: e.target.value } })}
                              placeholder={field}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="What your system needs to provide" hint="The eight things the receptionist asks it to do.">
                <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  <li>Find a patient by phone, or by full name plus date of birth, and return their id, name and date of birth.</li>
                  <li>Create a patient who is new to the clinic.</li>
                  <li>List doctors, and find open times for a doctor or a specialty between two dates.</li>
                  <li>List a patient's upcoming appointments.</li>
                  <li>Book, reschedule and cancel an appointment. Reply with HTTP 409 if the time was just taken.</li>
                  <li>Times are ISO 8601 with a time-zone offset, for example 2026-10-20T09:30:00+11:00.</li>
                  <li>Every write carries an Idempotency-Key header, so a repeated request must not double-book.</li>
                </ul>
              </Section>

              {testSection}
            </div>
          )}

          <Button onClick={() => save.mutate()} disabled={!canSave} data-testid="button-save-connector">
            {save.isPending ? "Saving…" : "Save integration"}
          </Button>
          {needsSecret && <p className="text-xs text-muted-foreground">Enter the token or key for your system to save.</p>}
        </CardContent>
      </Card>

      {mode === "internal" && <ProvidersPanel />}
    </div>
  );
}
